import GSAP from "gsap";
import Lenis from "lenis";
class App {
  constructor() {
    this.init();
    window.addEventListener("pageshow", (event) => {
      const nav = document.querySelector("nav");
      const fadeItem = document.querySelectorAll(".fade-header");
      const openBtn = document.querySelector(
        ".center__nav__menu__button__open",
      );
      const closeBtn = document.querySelector(
        ".center__nav__menu__button__close",
      );
      if (event.persisted) {
        GSAP.set(fadeItem, {
          opacity: 0,
          y: 50,
        });
        this.lenis.start();
        if (nav.classList.contains("menu-open")) {
          nav.classList.add("menu-close");
          nav.style.display = "none";
          closeBtn.style.display = "none";
          openBtn.style.display = "block";
        }
      }
    });
  }
  init() {
    this.preloader();
    this.preloaderProjectPage();
    this.initLenis();
    this.headerMenu();
    this.designStoreyLogoLineDraw();
    this.footerLogoLineDraw();
    this.initLazyImages({
      rootMargin: "-100px 0px",
      threshold: 0.01,
    });
    this.fadeUpAnimation();
    this.scrollToHashOnLoad(this.lenis);
    this.heroSectionArrowDropDown();
  }



  preloader() {
    const preloader = document.querySelector(".preloader");
    if (!preloader) return;

    // -----------------------------------------------------------------
    // Local helper — scoped entirely inside this method. Tracks whether
    // this visitor has already seen the preloader (shared across home +
    // project pages, since both use this same .preloader partial and
    // call this same method).
    // -----------------------------------------------------------------
    const PreloaderVisibility = {
      _memoryFallback: new Set(),

      hasBeenSeen(key) {
        try {
          return window.localStorage.getItem(key) === "true";
        } catch (err) {
          // localStorage blocked (private mode, quota, extensions, etc.)
          return this._memoryFallback.has(key);
        }
      },

      markAsSeen(key) {
        try {
          window.localStorage.setItem(key, "true");
        } catch (err) {
          this._memoryFallback.add(key);
        }
      },
    };

    // First-time-only gate. Bump the version suffix (v1 -> v2) if you
    // redesign this preloader and want it to play again for everyone.
    const PRELOADER_STORAGE_KEY = "site_preloader_home";

    const centerNavMenuButton = document.querySelectorAll(
      ".center__nav__menu__button",
    );
    const rightNavSearchButton = document.querySelectorAll(
      ".right__nav__search__button",
    );

    if (PreloaderVisibility.hasBeenSeen(PRELOADER_STORAGE_KEY)) {
      preloader.style.display = "none";
      GSAP.set(rightNavSearchButton, { opacity: 1 });
      GSAP.set(centerNavMenuButton, { opacity: 1 });
      return;
    }

    PreloaderVisibility.markAsSeen(PRELOADER_STORAGE_KEY);
    // -----------------------------------------------------------------

    // Elements
    const preloaderLogoTop = preloader.querySelector(".preloader__logo__top");
    const preloaderLogoBottom = preloader.querySelector(
      ".preloader__logo__bottom",
    );
    const preloaderRectLine = preloader.querySelector(
      ".preloader__center__line svg",
    );
    const preloaderText = preloader.querySelector(
      ".preloader__center__line__text",
    );
    const preloaderMaskContainer = preloader.querySelector(
      ".preloader__center__image__scale__mask",
    );

    const images = preloader.querySelectorAll(
      ".preloader__center__image__scale__imagebox img",
    );

    function generateSwitchPoints(count) {
      if (count <= 1) return [0];
      return Array.from({ length: count }, (_, i) => i / (count - 1));
    }

    const config = {
      preloaderText: {
        duration: 1.3,
        delay: 0.8,
        ease: "power2.out",
      },
      preloaderRectLine: {
        duration: 0.3,
        ease: "power2.out",
      },
      preloaderMaskContainer: {
        duration: 1,
        ease: "0.23, 0, 0.11, 1",
      },
      preloaderLogo: {
        duration: 1,
        ease: "0.23, 0, 0.11, 1",
      },
      preloaderLogoMoveValues: {
        padding: window.innerWidth > 768 ? 160 : 120,
      },
      preloaderImageChange: {
        duration: 2.1,
        ease: "0, 0, 0.58, 1",
      },
      imageSwitchPoints: generateSwitchPoints(images.length),
      preloaderMaskImageExpandFull: {
        duration: 1.5,
        delay: 0.83,
        ease: "0.34, -0.01, 0.48, 0.99",
      },
      preloaderLogoFadeOut: {
        duration: 2.3,
        delay: 0.83,
        ease: "power2.out",
      },
      preloaderFadeOut: {
        duration: 0.5,
      },
    };

    GSAP.set(rightNavSearchButton, { opacity: 0 });
    GSAP.set(centerNavMenuButton, { opacity: 0 });

    const preloaderTimeline = GSAP.timeline({
      onStart: () => {
        this.lenis.stop();
      },
      onComplete: () => {
        GSAP.to(preloader, {
          opacity: 0,
          onStart: () => {
            this.lenis.start();
          },
          ...config.preloaderFadeOut,
          onComplete: () => {
            preloader.style.display = "none";
          },
        });

        GSAP.to(rightNavSearchButton, { opacity: 1 });
        GSAP.to(centerNavMenuButton, { opacity: 1 });
      },
    });

    preloaderTimeline.to(preloaderText, {
      opacity: 0,
      ...config.preloaderText,
      onComplete: () => {
        preloaderText.style.display = "none";
      },
    });

    preloaderTimeline.from(preloaderMaskContainer, {
      scaleX: 0,
      ...config.preloaderMaskContainer,
    });

    preloaderTimeline.from(
      preloaderRectLine.querySelector("path"),
      {
        scaleX: 0,
        ...config.preloaderRectLine,
      },
      "<",
    );

    preloaderTimeline.to(preloaderMaskContainer, {
      maskSize: window.innerWidth > 1024 ? "30% 40%" : "80% 60%",
      ...config.preloaderMaskContainer,
      onStart: () => {
        preloaderRectLine.style.display = "none";
        preloaderMaskContainer.style.maskSize = "14% 0.2%";
      },
    });

    preloaderTimeline.to(
      preloaderLogoTop,
      {
        y: -window.innerHeight / 2 + config.preloaderLogoMoveValues.padding,
        ...config.preloaderLogo,
      },
      "<",
    );
    preloaderTimeline.to(
      preloaderLogoBottom,
      {
        y: window.innerHeight / 2 - config.preloaderLogoMoveValues.padding,
        ...config.preloaderLogo,
      },
      "<",
    );

    preloaderTimeline.to(images, {
      ...config.preloaderImageChange,
      onUpdate: function () {
        const progress = this.progress();
        images.forEach((slide, i) => {
          if (i == images.length) return;
          const shouldBeActive =
            progress >= config.imageSwitchPoints[i] &&
            (i === images.length - 1 ||
              progress < config.imageSwitchPoints[i + 1]);

          slide.classList.toggle("is-active", shouldBeActive);
        });
      },
    });

    preloaderTimeline.to(preloaderMaskContainer, {
      maskSize: "100% 100%",
      ...config.preloaderMaskImageExpandFull,
    });
    preloaderTimeline.to(
      images[images.length - 1],
      {
        width: "100%",
        height: "100%",
        ...config.preloaderMaskImageExpandFull,
      },
      "<",
    );
    preloaderTimeline.to(
      [preloaderLogoTop, preloaderLogoBottom],
      {
        opacity: 0,
        ...config.preloaderLogoFadeOut,
      },
      "<",
    );
  }

  headerMenu() {
    const nav = document.querySelector("nav");
    const toggleBtn = document.querySelector(".center__nav__menu__button");
    const menuLogoLine = document.querySelector(".menu__logo__center__line");
    const fadeItem = document.querySelectorAll(".fade-header");
    const hamburgerLines = toggleBtn.querySelectorAll("line");

    if (!nav || !toggleBtn || !menuLogoLine) return;

    let isOpen = false;
    let openTl = null;
    let closeTl = null;

    toggleBtn.addEventListener("click", () => {
      // Kill any running timelines
      if (openTl) openTl.kill();
      if (closeTl) closeTl.kill();

      if (!isOpen) {
        // OPEN MENU
        this.lenis.stop();
        document.body.style.overflow = "hidden";

        // Force reset everything to starting state immediately
        GSAP.set(nav, { opacity: 0 });
        GSAP.set(fadeItem, { opacity: 0, y: 50 });
        GSAP.set(menuLogoLine, { width: "0%" });
        GSAP.set(hamburgerLines[0], { y: 0, rotation: 0, opacity: 1 });
        GSAP.set(hamburgerLines[1], { opacity: 1 });
        GSAP.set(hamburgerLines[2], { y: 0, rotation: 0, opacity: 1 });

        nav.classList.remove("menu-close");
        nav.classList.add("menu-open");
        nav.style.display = "block";

        openTl = GSAP.timeline();

        // Hamburger to Cross Animation
        openTl.to(
          hamburgerLines[0],
          {
            y: 4,
            rotation: 45,
            transformOrigin: "50% 50%",
            duration: 0.3,
            ease: "power2.inOut",
          },
          0,
        );
        openTl.to(
          hamburgerLines[1],
          {
            opacity: 0,
            duration: 0.2,
            ease: "power2.inOut",
          },
          0,
        );
        openTl.to(
          hamburgerLines[2],
          {
            y: -4,
            rotation: -45,
            transformOrigin: "50% 50%",
            duration: 0.3,
            ease: "power2.inOut",
          },
          0,
        );

        openTl.to(
          nav,
          {
            opacity: 1,
            duration: 0.01,
            ease: "none",
          },
          0,
        );
        openTl.to(fadeItem, {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          duration: 1,
        });
        openTl.fromTo(
          menuLogoLine,
          {
            width: "0%",
          },
          {
            width: "100%",
            duration: 0.3,
            ease: "power2.out",
          },
          "-=0.8",
        );

        isOpen = true;
      } else {
        // CLOSE MENU
        this.lenis.start();
        document.body.style.overflow = "auto";

        // Force reset fade items and nav immediately
        GSAP.set(fadeItem, { opacity: 0, y: 50 });

        nav.classList.remove("menu-open");
        nav.classList.add("menu-close");

        closeTl = GSAP.timeline({
          onComplete: () => {
            nav.style.display = "none";
          },
        });

        // Un-rotate and move back
        closeTl.to(
          hamburgerLines[0],
          {
            y: 0,
            rotation: 0,
            transformOrigin: "50% 50%",
            duration: 0.3,
            ease: "power2.inOut",
          },
          0,
        );
        closeTl.to(
          hamburgerLines[1],
          {
            opacity: 1,
            duration: 0.2,
            ease: "power2.inOut",
          },
          0.1,
        );
        closeTl.to(
          hamburgerLines[2],
          {
            y: 0,
            rotation: 0,
            transformOrigin: "50% 50%",
            duration: 0.3,
            ease: "power2.inOut",
          },
          0,
        );

        closeTl.to(
          nav,
          {
            opacity: 0,
            duration: 0.01,
            ease: "none",
          },
          0,
        );

        isOpen = false;
      }
    });
  }
  designStoreyLogoLineDraw() {
    const logoContainer = document.querySelector(
      ".about__brand__divider__section__center__logo",
    );
    if (!logoContainer) return;
    const rectLine = logoContainer.querySelector(
      ".brand__divider__rect__logo__line",
    );
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((el) => {
          if (el.isIntersecting) {
            GSAP.to(rectLine, {
              scaleX: 1,
              delay: 0.6,
              duration: 0.3,
              ease: "power1.out",
            });
          }
        });
      },
      { rootMargin: "0px 0px -1% 0px" },
    );
    observer.observe(logoContainer);
  }
  footerLogoLineDraw() {
    const logoContainer = document.querySelector(
      ".design__storey__footer__logo",
    );
    if (!logoContainer) return;
    const rectLine = logoContainer.querySelector(".rect__logo__line");
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((el) => {
          if (el.isIntersecting) {
            GSAP.to(rectLine, {
              scaleX: 1,
              delay: 0.6,
              duration: 0.3,
              ease: "power1.out",
            });
          }
        });
      },
      { rootMargin: "0px 0px -1% 0px" },
    );
    observer.observe(logoContainer);
  }
  preloaderProjectPage() {
    const preloader = document.querySelector(".preloader__project");
    if (!preloader) return;

    // -----------------------------------------------------------------
    // Local helper — same shape as the one in preloader(). Deliberately
    // duplicated here (see "pros/cons" note) rather than shared, to keep
    // each method fully self-contained.
    // -----------------------------------------------------------------
    const PreloaderVisibility = {
      _memoryFallback: new Set(),

      hasBeenSeen(key) {
        try {
          return window.localStorage.getItem(key) === "true";
        } catch (err) {
          return this._memoryFallback.has(key);
        }
      },

      markAsSeen(key) {
        try {
          window.localStorage.setItem(key, "true");
        } catch (err) {
          this._memoryFallback.add(key);
        }
      },
    };

    // Same key as the homepage preloader — "first time a user visits the
    // website" is one flag for the whole site, not one per page. Whichever
    // preloader (home or project) the visitor hits first marks this seen,
    // and neither will replay after that.
    const PRELOADER_STORAGE_KEY = "site_preloader_project";

    const centerNavMenuButton = document.querySelectorAll(
      ".center__nav__menu__button",
    );
    const rightNavSearchButton = document.querySelectorAll(
      ".right__nav__search__button",
    );

    if (PreloaderVisibility.hasBeenSeen(PRELOADER_STORAGE_KEY)) {
      preloader.style.display = "none";
      GSAP.set(rightNavSearchButton, { opacity: 1 });
      GSAP.set(centerNavMenuButton, { opacity: 1 });
      return;
    }

    PreloaderVisibility.markAsSeen(PRELOADER_STORAGE_KEY);
    // -----------------------------------------------------------------

    // Elements
    const preloaderLogoTop = preloader.querySelector(".preloader__logo__top");
    const preloaderLogoBottom = preloader.querySelector(
      ".preloader__logo__bottom",
    );
    const preloaderRectLine = preloader.querySelector(
      ".preloader__center__line svg",
    );
    const preloaderText = preloader.querySelector(
      ".preloader__center__line__text",
    );
    const preloaderMaskContainer = preloader.querySelector(
      ".preloader__center__image__scale__mask",
    );

    // Config — same tuned values as the main preloader()
    const config = {
      preloaderText: {
        duration: 1.3,
        delay: 0.8,
        ease: "power2.out",
      },
      preloaderRectLine: {
        duration: 0.3,
        ease: "power2.out",
      },
      preloaderMaskContainer: {
        duration: 1,
        ease: "0.78, 0, 0.16, 1",
      },
      preloaderLogo: {
        duration: 1,
        ease: "0.78, 0, 0.16, 1",
      },
      preloaderLogoMoveValues: {
        padding: window.innerWidth > 768 ? 160 : 120,
      },
      preloaderMaskImageExpandFull: {
        duration: 2.3,
        delay: 0.4,
        ease: "power4.inOut",
      },
      preloaderLogoFadeOut: {
        duration: 1.5,
        delay: 0.83,
        ease: "0.46, 0, 0.5, 0.99",
      },
      preloaderFadeOut: {
        duration: 0.5,
      },
    };

    GSAP.set(rightNavSearchButton, { opacity: 0 });
    GSAP.set(centerNavMenuButton, { opacity: 0 });

    // Timeline
    const preloaderTimeline = GSAP.timeline({
      onStart: () => {
        this.lenis.stop();
      },
      onComplete: () => {
        GSAP.to(preloader, {
          opacity: 0,
          onStart: () => {
            this.lenis.start();
          },
          ...config.preloaderFadeOut,
          onComplete: () => {
            preloader.style.display = "none";
          },
        });

        GSAP.to(rightNavSearchButton, { opacity: 1 });
        GSAP.to(centerNavMenuButton, { opacity: 1 });
      },
    });

    // Animations
    preloaderTimeline.to(preloaderText, {
      opacity: 0,
      ...config.preloaderText,
      onComplete: () => {
        preloaderText.style.display = "none";
      },
    });

    preloaderTimeline.from(preloaderMaskContainer, {
      scaleX: 0,
      ...config.preloaderMaskContainer,
    });

    preloaderTimeline.from(
      preloaderRectLine.querySelector("path"),
      {
        scaleX: 0,
        ...config.preloaderRectLine,
      },
      "<",
    );

    preloaderTimeline.to(preloaderMaskContainer, {
      maskSize: window.innerWidth > 1024 ? "30% 40%" : "80% 60%",
      ...config.preloaderMaskContainer,
      onStart: () => {
        preloaderRectLine.style.display = "none";
        preloaderMaskContainer.style.maskSize = "14% 0.2%";
      },
    });

    preloaderTimeline.to(
      preloaderLogoTop,
      {
        y: -window.innerHeight / 2 + config.preloaderLogoMoveValues.padding,
        ...config.preloaderLogo,
      },
      "<",
    );
    preloaderTimeline.to(
      preloaderLogoBottom,
      {
        y: window.innerHeight / 2 - config.preloaderLogoMoveValues.padding,
        ...config.preloaderLogo,
      },
      "<",
    );

    preloaderTimeline.to(preloaderMaskContainer, {
      maskSize: "100% 100%",
      ...config.preloaderMaskImageExpandFull,
    });
    preloaderTimeline.to(
      preloaderMaskContainer.querySelector("img"),
      {
        width: "100%",
        height: "100%",
        ...config.preloaderMaskImageExpandFull,
      },
      "<",
    );
    preloaderTimeline.to(
      [preloaderLogoTop, preloaderLogoBottom],
      {
        opacity: 0,
        ...config.preloaderLogoFadeOut,
      },
      "<",
    );
  }
  initLenis(options = {}) {
    const lenis = new Lenis({
      duration: 1.2, // scroll duration
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // smooth easing
      smoothWheel: true, // smooth mouse wheel
      wheelMultiplier: 1, // scroll speed
      touchMultiplier: 2, // touch scroll speed
      ...options,
    });

    // Integrate with GSAP ScrollTrigger if available
    if (window.ScrollTrigger) {
      lenis.on("scroll", ScrollTrigger.update);

      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });

      gsap.ticker.lagSmoothing(0);
    } else {
      // Standard raf loop without GSAP
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }
    this.lenis = lenis;
    // Expose API
    return {
      lenis,
      scrollTo: (target, options) => lenis.scrollTo(target, options),
      stop: () => lenis.stop(),
      start: () => lenis.start(),
      destroy: () => lenis.destroy(),
    };
  }
  initLazyImages(options = {}) {
    const config = {
      rootMargin: "50px 0px",
      threshold: 0.01,
      selector: "img[data-src]",
      ...options,
    };

    function getDominantColor(img, width, height) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const scale = Math.min(1, 100 / Math.max(width, height));
      canvas.width = Math.floor(width * scale) || 1;
      canvas.height = Math.floor(height * scale) || 1;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      const colorCounts = new Map();
      let maxScore = 0;
      let dominantColor = "rgb(224, 224, 224)";

      for (let i = 0; i < pixels.length; i += 8) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const a = pixels[i + 3];

        if (a < 128) continue;

        const brightness = (r + g + b) / 3;
        if (brightness < 20 || brightness > 240) continue;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const saturation = max === 0 ? 0 : (max - min) / max;
        if (saturation < 0.1) continue;

        const qr = Math.round(r / 12) * 12;
        const qg = Math.round(g / 12) * 12;
        const qb = Math.round(b / 12) * 12;
        const key = `${qr},${qg},${qb}`;

        const score = (colorCounts.get(key) || 0) + 1 + saturation * 2;
        colorCounts.set(key, score);

        if (score > maxScore) {
          maxScore = score;
          dominantColor = `rgb(${qr}, ${qg}, ${qb})`;
        }
      }

      return dominantColor;
    }

    // ─── ASYNC: Process each image independently ───
    function preloadAndSetParentHeights(selector) {
      const images = document.querySelectorAll(selector);

      images.forEach((img) => {
        if (img.getAttribute("data-ignore") == "") return;
        const src = img.dataset.src || img.src;
        if (!src) return;

        // Create a new Image for each — they load in parallel, independently
        const preloadImg = new Image();

        preloadImg.onload = () => {
          const { naturalWidth, naturalHeight } = preloadImg;
          const dominantColor = getDominantColor(
            preloadImg,
            naturalWidth,
            naturalHeight,
          );

          const wrapper = document.createElement("div");
          wrapper.className = "lazy-image-wrapper";
          wrapper.style.aspectRatio = `${naturalWidth} / ${naturalHeight}`;
          wrapper.style.backgroundColor = dominantColor;

          img.parentNode.insertBefore(wrapper, img);
          wrapper.appendChild(img);
        };

        preloadImg.onerror = () => {
          console.error("Failed to preload image for sizing:", src);
        };

        // Start loading immediately — non-blocking
        preloadImg.src = src;
      });
    }

    function prepareImages(selector) {
      const images = document.querySelectorAll(selector);
      images.forEach((img) => {
        if (img.getAttribute("data-ignore") == "") return;
        if (img.src && !img.dataset.src) {
          img.dataset.src = img.src;
          img.src = "";
        }
      });
      return images;
    }

    function loadImage(img) {
      const src = img.dataset.src;
      if (!src) return;
      img.src = src;
      img.onload = () => {
        img.classList.add("loaded");
        delete img.dataset.src;
      };
      img.onerror = () => {
        img.classList.add("error");
        console.error("Failed to load image:", src);
      };
    }

    function onIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          loadImage(img);
          observer.unobserve(img);
        }
      });
    }

    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: config.rootMargin,
      threshold: config.threshold,
    });

    function observeImages(selector = config.selector) {
      const images = document.querySelectorAll(selector);
      images.forEach((img) => {
        if (img.getAttribute("data-ignore") == "") return;
        observer.observe(img);
      });
      return images;
    }

    function destroy() {
      observer.disconnect();
    }

    // ─── Initialization ───
    preloadAndSetParentHeights("img[data-src], img:not([data-src])");
    const preparedImages = prepareImages("img[data-src], img:not([data-src])");
    const observedImages = observeImages(config.selector);

    return {
      observer,
      observeImages,
      prepareImages,
      loadImage,
      destroy,
      getObservedCount: () => observedImages.length,
      preloadAndSetParentHeights,
    };
  }
  fadeUpAnimation() {
    const elements = document.querySelectorAll(".fade");
    if (!elements.length) return;

    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          GSAP.to(entry.target, {
            opacity: 1,
            y: 0,
            delay: index * 0.2,
            duration: 1,
          });
        }
      });
    });
    elements.forEach((element) => {
      observer.observe(element);
    });
  }
  scrollToHashOnLoad(lenis) {
    const hash = window.location.hash;
    if (!hash) return;

    setTimeout(() => {
      const target = document.querySelector(hash);
      if (target && lenis) {
        lenis.scrollTo(target, { offset: 0, duration: 1.2 });
      } else if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    }, 500); // wait for layout/images/fonts to settle before scrolling
  }
  heroSectionArrowDropDown() {
    const arrow = document.querySelector(".hero__section__dropdown__arrow svg");
    if (!arrow) return;
    arrow.addEventListener("click", () => {
      this.lenis.scrollTo(window.innerHeight, 1);
    });
  }
}

new App();
